import { Component, computed, ElementRef, HostListener, Inject, PLATFORM_ID, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

import { CoreModule } from '@core/core.module';
import { TENANTS_STATE_TOKEN, TenantsState, TenantInfo } from '@core/token/tenant.token';

import { ConviviaLogoComponent } from '../convivia-logo/convivia-logo.component';

@Component({
  selector: 'app-tenant-switcher',
  templateUrl: './tenant-switcher.component.html',
  styleUrls: ['./tenant-switcher.component.scss'],
  imports: [CoreModule, ConviviaLogoComponent],
})
export class TenantSwitcherComponent {
  protected readonly state: TenantsState;
  protected readonly dropdownOpen = signal(false);
  protected readonly transitionTenant = signal<TenantInfo | null>(null);
  protected readonly hasError = signal(false);

  protected readonly currentTenant = computed(() => {
    return this.state.tenants.find(t => t.id === this.state.currentTenantId) ?? null;
  });

  protected readonly hasMultipleTenants = computed(() => this.state.tenants.length > 1);

  constructor(
    private readonly elementRef: ElementRef,
    @Inject(PLATFORM_ID) private readonly platform: string,
    @Inject(TENANTS_STATE_TOKEN) private readonly injectedState: TenantsState
  ) {
    this.state = injectedState;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!isPlatformBrowser(this.platform)) return;
    if (this.dropdownOpen() && !this.elementRef.nativeElement.contains(event.target as Node)) {
      this.dropdownOpen.set(false);
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.dropdownOpen()) {
      this.dropdownOpen.set(false);
    }
  }

  /**
   * When selectTenant() navigates away, the browser can snapshot the DOM/JS state with the
   * transition overlay still visible (it was set right before `location.href` changed). If the
   * user then hits Back and the browser restores that page from the bfcache instead of
   * re-running app bootstrap, `event.persisted` is true and the overlay would otherwise stay
   * stuck forever, since no new navigation happens to reset it.
   */
  @HostListener('window:pageshow', ['$event'])
  onPageShow(event: PageTransitionEvent): void {
    if (!event.persisted) return;
    this.transitionTenant.set(null);
    this.hasError.set(false);
  }

  protected toggleDropdown(): void {
    this.dropdownOpen.update(v => !v);
  }

  protected selectTenant(tenant: TenantInfo): void {
    this.dropdownOpen.set(false);
    this.transitionTenant.set(tenant);
    this.hasError.set(false);

    if (!isPlatformBrowser(this.platform)) return;

    try {
      globalThis.location.href = this.buildTenantUrl(tenant);
    } catch {
      this.hasError.set(true);
    }
  }

  /** Siempre redirige a `/app` en el dominio del tenant destino, descartando la ruta actual. */
  private buildTenantUrl(tenant: TenantInfo): string {
    const { protocol, port } = globalThis.location;
    const portSuffix = port ? `:${port}` : '';

    return `${protocol}//${tenant.domain.replace(/\/$/, '')}${portSuffix}/app`;
  }

  protected cancelTransition(): void {
    this.transitionTenant.set(null);
    this.hasError.set(false);
  }
}
